


import { useState, useEffect } from 'react';
import axios from 'axios';
import { FiEye, FiTrash2, FiUser, FiTrendingUp, FiAward } from 'react-icons/fi';
import UserPerformanceDetails from './UserPerformanceDetails';

function UserManagement() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showUserDetails, setShowUserDetails] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://firstaidba.vercel.app/api/user/admin/users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://firstaidba.vercel.app/api/user/admin/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedUser(response.data);
      setShowUserDetails(true);
    } catch (err) {
      console.error('Error fetching user details:', err);
      setError('Failed to fetch user details');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (showUserDetails && selectedUser) {
    return (
      <UserPerformanceDetails 
        user={selectedUser} 
        onBack={() => setShowUserDetails(false)}
      />
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">User Management</h2>
        <span className="text-gray-600">{users.length} users</span>
      </div>

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded">
          <p>{error}</p>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full table-auto">
          <thead>
            <tr className="bg-gray-50 text-left">
              <th className="px-4 py-3 font-semibold text-gray-700">User</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Email</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Phone</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Points</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Scenarios</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Joined</th>
              <th className="px-4 py-3 font-semibold text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              // Calculate points based on completed scenarios if points field is missing/wrong
              const userPoints = user.points || (user.completedScenarios?.length || 0) * 20;
              const completedScenariosCount = user.completedScenarios?.length || 0;
              
              return (
                <tr key={user._id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center">
                        <FiUser className="text-purple-600" />
                      </div>
                      <span className="font-medium text-gray-800">
                        {user.email.split('@')[0]}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-700">{user.email}</td>
                  <td className="px-4 py-3 text-gray-700">{user.phone}</td>
                  <td className="px-4 py-3">
                    <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 py-1 px-2 rounded-full text-sm">
                      <FiAward className="text-yellow-600" />
                      {userPoints}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="bg-blue-100 text-blue-800 py-1 px-2 rounded-full text-sm">
                      {completedScenariosCount} completed
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-sm">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => viewUserDetails(user._id)}
                      className="flex items-center gap-2 bg-purple-100 text-purple-700 py-2 px-3 rounded-lg hover:bg-purple-200 transition-colors"
                    >
                      <FiEye size={16} />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {users.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <FiUser className="mx-auto text-4xl mb-3 text-gray-400" />
          <p>No users found</p>
        </div>
      )}
    </div>
  );
}

export default UserManagement;